import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  children?: { id: string; label: string; path: string }[];
}

const menuItems: MenuItem[] = [
  { id: 'overview', label: '数据概览', icon: 'ri-dashboard-line', path: '/overview' },
  {
    id: 'users',
    label: '用户管理',
    icon: 'ri-user-line',
    children: [{ id: 'user-list', label: '用户列表', path: '/users/list' }],
  },
  {
    id: 'tenants',
    label: '租户管理',
    icon: 'ri-building-line',
    children: [{ id: 'tenant-list', label: '租户列表', path: '/tenants/list' }],
  },
  {
    id: 'resources',
    label: '资源管理',
    icon: 'ri-server-line',
    children: [
      { id: 'servers', label: '服务器管理', path: '/resources/servers' },
      { id: 'templates', label: '云电脑模板', path: '/resources/templates' },
      { id: 'datacenters', label: '机房管理', path: '/resources/datacenters' },
      { id: 'apps', label: '应用管理', path: '/resources/apps' },
    ],
  },
  {
    id: 'monitoring',
    label: '监测',
    icon: 'ri-eye-line',
    children: [{ id: 'faults', label: '故障列表', path: '/monitoring/faults' }],
  },
  {
    id: 'products',
    label: '商品管理',
    icon: 'ri-shopping-bag-line',
    children: [
      { id: 'product-list', label: '商品列表', path: '/products/list' },
      { id: 'disk-products', label: '数据盘商品', path: '/products/disks' },
      { id: 'recharge', label: '充值管理', path: '/products/recharge' },
      { id: 'categories', label: '商品分类', path: '/products/categories' },
    ],
  },
  {
    id: 'orders',
    label: '订单管理',
    icon: 'ri-file-list-line',
    children: [
      { id: 'order-list', label: '订单列表', path: '/orders/list' },
      { id: 'after-sales', label: '售后订单', path: '/orders/after-sales' },
      { id: 'order-config', label: '订单配置', path: '/orders/config' },
    ],
  },
  {
    id: 'points',
    label: '积分管理',
    icon: 'ri-coin-line',
    children: [
      { id: 'point-operations', label: '积分操作列表', path: '/points/operations' },
      { id: 'point-recharge', label: '积分充值', path: '/points/recharge' },
    ],
  },
  {
    id: 'user-resources',
    label: '用户资源',
    icon: 'ri-computer-line',
    children: [
      { id: 'cloud-pcs', label: '云电脑', path: '/user-resources/cloud-pcs' },
      { id: 'cloud-disks', label: '云硬盘', path: '/user-resources/cloud-disks' },
    ],
  },
  {
    id: 'terminals',
    label: '终端管理',
    icon: 'ri-device-line',
    children: [
      { id: 'venues', label: '场所管理', path: '/terminals/venues' },
      { id: 'thin-clients', label: '瘦终端管理', path: '/terminals/thin-clients' },
    ],
  },
  {
    id: 'billing',
    label: '账单中心',
    icon: 'ri-bill-line',
    children: [
      { id: 'recharge-records', label: '充值记录', path: '/billing/recharge-records' },
      { id: 'bill-summary', label: '账单汇总', path: '/billing/summary' },
      { id: 'bill-details', label: '账单明细', path: '/billing/details' },
    ],
  },
  {
    id: 'permissions',
    label: '权限管理',
    icon: 'ri-shield-user-line',
    children: [
      { id: 'roles', label: '角色管理', path: '/permissions/roles' },
      { id: 'accounts', label: '账号管理', path: '/permissions/accounts' },
      { id: 'departments', label: '部门管理', path: '/permissions/departments' },
    ],
  },
  {
    id: 'notifications',
    label: '消息通知',
    icon: 'ri-notification-line',
    children: [
      { id: 'notification-list', label: '通知列表', path: '/notifications/list' },
      { id: 'notification-management', label: '通知管理', path: '/notifications/management' },
      { id: 'send-records', label: '发送记录', path: '/notifications/records' },
    ],
  },
  {
    id: 'system',
    label: '系统设置',
    icon: 'ri-settings-line',
    children: [
      { id: 'operation-logs', label: '操作日志', path: '/system/logs' },
      { id: 'system-config', label: '系统配置', path: '/system/config' },
      { id: 'system-maintenance', label: '系统维护', path: '/system/maintenance' },
      { id: 'scheduled-tasks', label: '定时任务管理', path: '/system/tasks' },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['overview']);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus((prev) =>
      prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]
    );
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const isParentActive = (children?: { path: string }[]) => {
    if (!children) return false;
    return children.some((child) => location.pathname === child.path);
  };

  return (
    <div className="w-56 bg-white border-r border-gray-200 h-screen flex flex-col fixed left-0 top-0">
      <div className="h-16 flex items-center px-5 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
            <i className="ri-computer-line text-white text-lg"></i>
          </div>
          <span className="text-lg font-semibold text-gray-800">Desk 运营平台</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {menuItems.map((item) => (
          <div key={item.id} className="mb-1">
            {item.children ? (
              <>
                <button
                  onClick={() => toggleMenu(item.id)}
                  className={`w-full flex items-center justify-between px-5 py-2.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isParentActive(item.children)
                      ? 'text-teal-600 bg-teal-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`${item.icon} text-base`}></i>
                    <span>{item.label}</span>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform ${
                      expandedMenus.includes(item.id) ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {expandedMenus.includes(item.id) && (
                  <div className="bg-gray-50">
                    {item.children.map((child) => (
                      <Link
                        key={child.id}
                        to={child.path}
                        className={`block pl-14 pr-5 py-2 text-sm transition-colors whitespace-nowrap ${
                          isActive(child.path)
                            ? 'text-teal-600 bg-teal-50 font-medium'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <Link
                to={item.path!}
                className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-teal-600 bg-teal-50 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className={`${item.icon} text-base`}></i>
                <span>{item.label}</span>
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
